/**
 * NewProjectProvider — the single mount point for the New Project dialog
 * (docs/plans/2026-07-12_PROJECT_ACCESS_ON_CREATE.md, §3.4).
 *
 * Every scattered project-creation entry point (DriveView "New project", the
 * empty-workspace picker, the workspace menu bar, the mobile overlay, the
 * new-workspace drop target) funnels through `useNewProject().open(...)` instead
 * of calling create-untitled directly. Rendering the dialog once here (rather
 * than per-site) keeps a single source of truth AND centralizes the D7 rule:
 * personal (non-org) tenants skip the audience dialog entirely.
 *
 * Mounted near the app root with the ambient auth/tenant context as props.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { request } from '@papan/api-client'
import { NewProjectDialog } from './NewProjectDialog'
import { NewProjectContext, type NewProjectCtx, type OpenNewProjectOpts } from './NewProjectContext'

export { useNewProject, type OpenNewProjectOpts } from './NewProjectContext'

export function NewProjectProvider({ isOrg, tenantId, tenantName, currentUserEmail, controllerRef, children }: {
  isOrg: boolean
  tenantId: string
  tenantName?: string
  currentUserEmail: string
  /** Lets a component OUTSIDE the provider (e.g. AppLayout, which renders it and
   *  so can't consume the context) trigger the dialog imperatively. */
  controllerRef?: { current: NewProjectCtx | null }
  children: ReactNode
}) {
  const [opts, setOpts] = useState<OpenNewProjectOpts | null>(null)

  const open = useCallback((o: OpenNewProjectOpts) => {
    // Personal tenants skip the audience dialog (D7) — but ONLY for projects.
    // Mini-apps always show the dialog (the standalone/for-a-project choice
    // applies on personal too).
    if (!isOrg && o.entityKind !== 'miniapp') {
      if (o.variant === 'promote' && o.promoteFolderId && o.promoteNewName) {
        // Restrict-in-place is meaningless for a single-member tenant — just
        // apply the gaining rename; the server chokepoint stamps the marker.
        request(`/api/files/${o.promoteFolderId}`, { method: 'PUT', body: JSON.stringify({ name: o.promoteNewName }) })
          .then(() => o.onDone(o.promoteFolderId!))
          .catch(() => {})
        return
      }
      if (o.autoCreatePersonal) {
        // No dialog (§3.5) — create a private project directly. Named after the
        // caller's initialName when given (a loose-file wrapper), else Untitled.
        // Re-open reuse comes free: once the file opens as a tab, find-for-file
        // returns this wrapper next time rather than making another.
        request<{ folderId: string }>(`/api/workspaces`, {
          method: 'POST',
          body: JSON.stringify({ name: o.initialName?.trim() || 'Untitled', mode: 'private', access: { restricted: false, grants: [] } }),
        })
          .then((r) => o.onDone(r.folderId))
          .catch(() => {})
        return
      }
      // Fall through: show the dialog (degrades to name-only, no audience).
    }
    setOpts(o)
  }, [isOrg])

  // Expose open() to an out-of-tree caller via the optional controller ref.
  useEffect(() => {
    if (!controllerRef) return
    controllerRef.current = { open }
    return () => { controllerRef.current = null }
  }, [controllerRef, open])

  return (
    <NewProjectContext.Provider value={{ open, tenantName }}>
      {children}
      {opts && (
        <NewProjectDialog
          open
          onClose={() => setOpts(null)}
          isOrg={isOrg}
          tenantId={tenantId}
          tenantName={tenantName}
          currentUserEmail={currentUserEmail}
          entityKind={opts.entityKind}
          variant={opts.variant}
          initialName={opts.initialName}
          promoteFolderId={opts.promoteFolderId}
          promoteNewName={opts.promoteNewName}
          onDone={(fid) => { opts.onDone(fid) }}
        />
      )}
    </NewProjectContext.Provider>
  )
}
